import ReactDOM from "react-dom/client"
import { initFixPrompt } from "@fixprompt/browser"
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import App from "./App"
import { BrowserRouter, Routes, Route, Navigate, useSearchParams } from "react-router-dom"
import { FixPromptBoundary } from "./components/FixPromptBoundary"
import UpdateAvailableBanner from "./components/UpdateAvailableBanner"
import { AuthProvider } from "./context/AuthContext"
import { TimeFormatProvider } from "./context/TimeFormatContext"
import { LocationProvider } from "./context/LocationContext"
import ProtectedRoute from "./components/ProtectedRoute"
import RoleProtectedRoute from "./components/RoleProtectedRoute"
import SignupForm from "./pages/Signup"
import SignInForm from "./pages/Signin"
import DocsZapierPage from "./pages/docs/zapier"
import ServiceFlowDashboard from "./pages/dashboard-v2"
import ServiceFlowRequests from "./pages/Request"
import ServiceFlowSchedule from "./pages/schedule-v2"
import ServiceFlowJobs from "./pages/jobs-v2"
import WorkerAvailability from "./pages/worker-availability"
import EditSingleDateAvailability from "./pages/edit-single-date-availability"
import AvailableJobs from "./pages/available-jobs"
import JobDetails from "./pages/job-details-v2"
import ServiceFlowEstimates from "./pages/serviceflow-estimates"
import ServiceFlowRecurring from "./pages/serviceflow-recurring"
import ServiceFlowPayments from "./pages/serviceflow-payments"
import ServiceFlowInvoices from "./pages/serviceflow-invoices"
import ServiceFlowCustomers from "./pages/customers-v2"
import CustomerDetails from "./pages/customer-details-v2"
import InvoiceDetails from "./pages/invoice-details"
import InvoiceEdit from "./pages/invoice-edit"
import ServiceFlowTeam from "./pages/serviceflow-team"
import ServiceFlowServices from "./pages/serviceflow-services"
import ServiceFlowCoupons from "./pages/serviceflow-coupons"
import CreateCoupon from "./pages/create-coupon"
import ServiceFlowTerritories from "./pages/serviceflow-territories"
import TerritoryDetails from "./pages/territory-details"
import ZipManager from "./pages/zip-manager"
import Analytics from "./pages/analytics-v2"
import ServiceFlowOnlineBooking from "./pages/serviceflow-online-booking"
import ServiceFlowWebsiteEmbed from "./pages/serviceflow-website-embed"
import ServiceFlowSettings from "./pages/serviceflow-settings-v2"
import TeamMemberDetails from "./pages/team-member-details"
import TeamAvailabilityCalendar from "./pages/team-availability-calendar"
import UnifiedCalendar from "./pages/unified-calendar"
import Payroll from "./pages/payroll"
import StaffLocationsMap from "./pages/staff-locations-map"
import AddTeamMember from "./pages/add-team-member"
import ServiceDetails from "./pages/service-details"
import WhatsNewPage from "./pages/whats-new"
import HelpPage from "./pages/help"
import PublicBooking from "./pages/public-booking"
import PublicQuote from "./pages/public-quote"
import DemoPublicPages from "./pages/demo-public-pages"
import CustomerPortal from "./pages/customer-portal"
import PaymentSuccess from "./pages/payment-success"
import InvoiceDisplay from "./pages/invoice-display"
import PaymentProcessing from "./pages/payment-processing"

// Settings Pages
import FeedbackReviews from "./pages/settings/feedback-reviews"
import CommunicationHub from "./pages/settings/communication-hub"
import OpportunitiesSettings from "./pages/settings/opportunities-settings"
import AdminDashboard from "./pages/admin-dashboard"
import ClientTeamNotifications from "./pages/settings/client-team-notifications"
import JobAssignment from "./pages/settings/job-assignment"
import Availability from "./pages/settings/availability"
import ReschedulingCancellation from "./pages/settings/rescheduling-cancellation"
import Developers from "./pages/settings/developers"
import CalendarSyncing from "./pages/settings/calendar-syncing"
import GoogleSheetsSettings from "./pages/settings/google-sheets"
import BookingKoalaIntegration from "./pages/settings/booking-koala"
import DataImportPage from "./pages/settings/data-import"
import TaxesFees from "./pages/settings/taxes-fees"
import PaymentsSettings from "./pages/settings/payments"
import PayoutSettings from "./pages/settings/payout-settings"
import Invoicing from "./pages/settings/invoicing"
import ZenbookerSettings from "./pages/settings/zenbooker"
import LeadBridgeSettings from "./pages/settings/leadbridge"
import ProofPixIntegrationSettings from "./pages/settings/proofpix"
import ProofPixAuthorize from "./pages/proofpix-authorize"
import IdentityConflictsPage from "./pages/settings/identity-conflicts"
import ServiceAreas from "./pages/settings/service-areas"
import BookingQuoteRequests from "./pages/settings/booking-quote-requests"
import FieldApp from "./pages/settings/field-app"
import CreateJobPage from "./pages/createjob"
import ServiceFlowEstimatePage from "./pages/bookableestimate"
import BrandingSettings from "./pages/settings/branding"
import AccountDetails from "./pages/settings/account-details"
import BusinessProfile from "./pages/settings/business-profile"
import BillingSettings from "./pages/settings/billing"
import TwilioSettings from "./pages/settings/twilio"
import StripeConnectSettings from "./pages/settings/stripe-connect"
import NotificationTestingSettings from "./pages/settings/notification-testing"
import CreateRecurringBooking from "./pages/createjob"
import LocationAvailability from "./pages/settings/location-availability"
import QuoteRequestProcessing from "./pages/settings/quote-request-processing"
import JobFollowUp from "./pages/settings/job-follow-up"
import BookingRequestAcknowledgment from "./pages/settings/booking-request-acknowledgment"
import AppointmentCancelled from "./pages/settings/appointment-cancelled"
import Enroute from "./pages/settings/enroute"
import RecurringBookingCancelled from "./pages/settings/recurring-booking-cancelled"
import PaymentReceipt from "./pages/settings/payment-receipt"
import Invoice from "./pages/settings/invoice"
import ContactCustomer from "./pages/settings/contact-customer"
import AppointmentConfirmation from "./pages/settings/appointment-confirmation"
import Estimate from "./pages/settings/estimate"
import AppointmentRescheduled from "./pages/settings/appointment-rescheduled"
import AppointmentReminder from "./pages/settings/appointment-reminder"
import AssignedJobCancelled from "./pages/settings/assigned-job-cancelled"
import AssignedJobRescheduled from "./pages/settings/assigned-job-rescheduled"
import TeamMemberInvite from "./pages/settings/team-member-invite"
import RecurringAssignment from "./pages/settings/recurring-assignment"
import JobOffer from "./pages/settings/job-offer"
import JobAssignmentTeam from "./pages/settings/job-assignment-team"
import TeamMemberSignup from "./pages/team-member-signup"
import ResetPasswordPage from "./pages/reset-password"
import DropdownMultiselectDemo from "./pages/dropdown-multiselect-demo"
import ImportDataPage from "./pages/import-data"
import ImportJobsPage from "./pages/import-jobs"
import OpportunitiesPipeline from "./pages/opportunities-pipeline"
import OpportunityDetailsPage from "./pages/opportunity-details"
import TasksPage from "./pages/tasks"
import Communications from "./pages/communications-v2"
import ConnectedInboxes from "./pages/settings/ConnectedInboxes"
import LandingPageLegacy from "./pages/LandingPage"
import Notifications from "./pages/notifications"
import { CategoryProvider } from "./context/CategoryContext"
import AppLayout from "./components/app-layout"

// Initialize FixPrompt runtime error reporting before any rendering.
// Must come AFTER all imports — the ESLint `import/first` rule fails the
// production build if an executable statement is interleaved with imports.
const fpKey = process.env.REACT_APP_FIXPROMPT_KEY
if (fpKey) {
  initFixPrompt({
    projectKey: fpKey,
    source: 'service-flow-frontend-prod',
    service: 'service-flow-frontend',
    env: process.env.NODE_ENV === 'production' ? 'prod' : 'dev',
  })
}

// Preserve incoming query params when redirecting old import routes
// (/settings/booking-koala, /import-jobs, /import-customers) to the new
// generic /settings/data-import flow.
function RedirectToDataImport({ defaultParams = {} }) {
  const [search] = useSearchParams()
  const merged = new URLSearchParams(defaultParams)
  for (const [k, v] of search.entries()) merged.set(k, v)
  const qs = merged.toString()
  return <Navigate to={`/settings/data-import${qs ? `?${qs}` : ''}`} replace />
}

// Register the service worker once at boot so push subscriptions work
// for the team-member PWA and the offline shell takes effect.
// Production-only — in dev the SW would serve stale assets across HMR
// reloads and force devs to hard-refresh constantly. Older Safari without
// SW support simply does nothing.
if (process.env.NODE_ENV === 'production' && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(() => {})
  })
}

// Legacy /team-member/login → /signin. One unified login screen handles
// both owners and team members; this preserves ?email= and other params so
// bookmarks and the post-signup redirect keep working.
function LegacyTeamMemberLoginRedirect() {
  const [search] = useSearchParams()
  const qs = search.toString()
  return <Navigate to={`/signin${qs ? `?${qs}` : ''}`} replace />
}

// /lead/:leadId legacy redirect — preserves the id when routing to the
// new /opportunity/:opportunityId path. Uses window.location so it works
// whether or not react-router-dom's useParams is imported at module scope.
function LegacyLeadRedirect() {
  const m = window.location.pathname.match(/^\/lead\/(.+?)\/?$/)
  const id = m ? m[1] : null
  return <Navigate to={id ? `/opportunity/${id}` : '/opportunities'} replace />
}

// Shared React Query client. staleTime of 60s means list pages don't refetch
// on every mount within the same minute (Schedule/Jobs/Customers were
// re-pulling thousands of rows every navigation). gcTime keeps unmounted
// pages in cache for 5 min so back-nav is instant. Refetch on window focus
// is off because these pages don't need the aggressive freshness that
// react-query's default assumes.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000,
      gcTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
})

const root = ReactDOM.createRoot(document.getElementById("root"))
root.render(
  <QueryClientProvider client={queryClient}>
  <FixPromptBoundary>
  <UpdateAvailableBanner />
  <BrowserRouter style={{fontFamily: 'Montserrat', fontWeight: 500}}>
    <AuthProvider>
      <LocationProvider>
      <TimeFormatProvider>
      <CategoryProvider>
        <Routes>
      <Route index element={<App />} />
      <Route path="signup" element={<SignupForm />} />
      <Route path="signin" element={<SignInForm />} />
      <Route path="legacy-landing" element={<LandingPageLegacy />} />
      {/* ProofPix same-device authorize — public (handles own auth check + bounce to /signin?continue=...) */}
      <Route path="integrations/proofpix/authorize" element={<ProofPixAuthorize />} />
      {/* Public API docs portal — BookingKoala -> ServiceFlow Zapier integration.
          /api/docs is kept as a public alias because the Zapier marketplace
          review listed it as the integration's documentation URL (older
          submission referenced serviceflow.app/api/docs). Both paths serve
          the same docs page so inbound links from either side resolve. */}
      <Route path="docs/zapier" element={<DocsZapierPage />} />
      <Route path="docs/zapier/*" element={<DocsZapierPage />} />
      <Route path="api/docs" element={<DocsZapierPage />} />
      <Route path="api/docs/*" element={<DocsZapierPage />} />
      <Route element={<AppLayout />}>
      <Route path="/dashboard" element={<ProtectedRoute><ServiceFlowDashboard /></ProtectedRoute>} />
      <Route path="/request" element={<ProtectedRoute><ServiceFlowRequests /></ProtectedRoute>} />
      <Route path="/schedule" element={<ProtectedRoute><ServiceFlowSchedule /></ProtectedRoute>} />
      <Route path="/jobs" element={<ProtectedRoute><ServiceFlowJobs /></ProtectedRoute>} />
      <Route path="/availability" element={<ProtectedRoute><WorkerAvailability /></ProtectedRoute>} />
      <Route path="/availability/edit/:date" element={<ProtectedRoute><EditSingleDateAvailability /></ProtectedRoute>} />
      <Route path="/offers" element={<ProtectedRoute><AvailableJobs /></ProtectedRoute>} />
      <Route path="/notifications" element={<ProtectedRoute><Notifications /></ProtectedRoute>} />
      <Route path="/job/:jobId" element={<ProtectedRoute><JobDetails /></ProtectedRoute>} />
      <Route path="/estimates" element={<ProtectedRoute><ServiceFlowEstimates /></ProtectedRoute>} />
      <Route path="/recurring" element={<ProtectedRoute><ServiceFlowRecurring /></ProtectedRoute>} />
      <Route path="/recurring/create" element={<CreateRecurringBooking />} />
      <Route path="/payments" element={<ServiceFlowPayments />} />
      <Route path="/invoices" element={<ProtectedRoute><ServiceFlowInvoices /></ProtectedRoute>} />
      <Route path="/customers" element={<ServiceFlowCustomers />} />
      <Route path="/customer/:customerId" element={<ProtectedRoute><CustomerDetails /></ProtectedRoute>} />
      <Route path="/opportunities" element={<ProtectedRoute><OpportunitiesPipeline /></ProtectedRoute>} />
      <Route path="/opportunity/:opportunityId" element={<ProtectedRoute><OpportunityDetailsPage /></ProtectedRoute>} />
      {/* Legacy /leads and /lead/:leadId redirect to the new /opportunities URLs
          for bookmarks and any external links that still point at the old paths. */}
      <Route path="/leads" element={<Navigate to="/opportunities" replace />} />
      <Route path="/lead/:leadId" element={<LegacyLeadRedirect />} />
      <Route path="/tasks" element={<ProtectedRoute><TasksPage /></ProtectedRoute>} />
      <Route path="/communications" element={<ProtectedRoute><Communications /></ProtectedRoute>} />
      <Route path="/invoices/:invoiceId" element={<ProtectedRoute><InvoiceDetails /></ProtectedRoute>} />
      <Route path="/invoices/:invoiceId/edit" element={<ProtectedRoute><InvoiceEdit /></ProtectedRoute>} />
      <Route path="/team" element={<ServiceFlowTeam />} />
      <Route path="/team/:memberId" element={<ProtectedRoute><TeamMemberDetails /></ProtectedRoute>} />
      <Route path="/add-team-member" element={<AddTeamMember />} />
      <Route path="/team-availability" element={<ProtectedRoute><TeamAvailabilityCalendar /></ProtectedRoute>} />
      <Route path="/calendar" element={<ProtectedRoute><UnifiedCalendar /></ProtectedRoute>} />
      <Route path="/payroll" element={<ProtectedRoute><Payroll /></ProtectedRoute>} />
      <Route path="/staff-locations" element={<ProtectedRoute><StaffLocationsMap /></ProtectedRoute>} />
      <Route path="/services" element={<ServiceFlowServices />} />
      <Route path="/services/:serviceId" element={<ServiceDetails />} />
      <Route path="/services/:serviceId/:section" element={<ServiceDetails />} />
      <Route path="/coupons" element={<ServiceFlowCoupons />} />
      <Route path="/coupons/create" element={<CreateCoupon />} />
      <Route path="/territories" element={<ServiceFlowTerritories />} />
      <Route path="/territories/zip-manager" element={<ZipManager />} />
      <Route path="/territories/:territoryId" element={<TerritoryDetails />} />
      <Route path="/analytics" element={<RoleProtectedRoute path="/analytics"><Analytics /></RoleProtectedRoute>} />
      <Route path="/online-booking" element={<ServiceFlowOnlineBooking />} />
      <Route path="/embed" element={<ServiceFlowWebsiteEmbed />} />
      <Route path="/createjob" element={<RoleProtectedRoute path="/createjob"><CreateJobPage /></RoleProtectedRoute>} />
      <Route path="/bookable-estimate" element={<ServiceFlowEstimatePage />} />
      <Route path="/whats-new" element={<WhatsNewPage />} />
      <Route path="/help" element={<HelpPage />} />
      {/* Settings Routes */}
      <Route path="/settings" element={<ProtectedRoute><ServiceFlowSettings /></ProtectedRoute>} />
      <Route path="/settings/account" element={<ProtectedRoute><AccountDetails /></ProtectedRoute>} />
      <Route path="/settings/business-profile" element={<ProtectedRoute><BusinessProfile /></ProtectedRoute>} />
      <Route path="/settings/billing" element={<ProtectedRoute><BillingSettings /></ProtectedRoute>} />
      <Route path="/settings/twilio" element={<ProtectedRoute><TwilioSettings /></ProtectedRoute>} />
      <Route path="/settings/sms-settings" element={<ProtectedRoute><TwilioSettings /></ProtectedRoute>} />
      <Route path="/settings/stripe-connect" element={<ProtectedRoute><StripeConnectSettings /></ProtectedRoute>} />
      <Route path="/settings/branding" element={<ProtectedRoute><BrandingSettings /></ProtectedRoute>} />
      <Route path="/settings/feedback-reviews" element={<ProtectedRoute><FeedbackReviews /></ProtectedRoute>} />
      <Route path="/settings/communication-hub" element={<ProtectedRoute><CommunicationHub /></ProtectedRoute>} />
      <Route path="/settings/connected-inboxes" element={<ProtectedRoute><ConnectedInboxes /></ProtectedRoute>} />
      <Route path="/settings/opportunities" element={<ProtectedRoute><OpportunitiesSettings /></ProtectedRoute>} />
      <Route path="/settings/leads" element={<ProtectedRoute><Navigate to="/settings/opportunities" replace /></ProtectedRoute>} />
      <Route path="/admin" element={<AdminDashboard />} />
      <Route path="/settings/client-team-notifications" element={<ProtectedRoute><ClientTeamNotifications /></ProtectedRoute>} />
      <Route path="/settings/client-team-notifications/notification-testing" element={<ProtectedRoute><NotificationTestingSettings /></ProtectedRoute>} />
      <Route path="/settings/client-team-notifications/quote-request-processing" element={<ProtectedRoute><QuoteRequestProcessing /></ProtectedRoute>} />
      <Route path="/settings/client-team-notifications/job-follow-up" element={<ProtectedRoute><JobFollowUp /></ProtectedRoute>} />
      <Route path="/settings/client-team-notifications/booking-request-acknowledgment" element={<ProtectedRoute><BookingRequestAcknowledgment /></ProtectedRoute>} />
      <Route path="/settings/client-team-notifications/appointment-cancelled" element={<ProtectedRoute><AppointmentCancelled /></ProtectedRoute>} />
      <Route path="/settings/client-team-notifications/enroute" element={<ProtectedRoute><Enroute /></ProtectedRoute>} />
      <Route path="/settings/client-team-notifications/recurring-booking-cancelled" element={<ProtectedRoute><RecurringBookingCancelled /></ProtectedRoute>} />
      <Route path="/settings/client-team-notifications/payment-receipt" element={<ProtectedRoute><PaymentReceipt /></ProtectedRoute>} />
      <Route path="/settings/client-team-notifications/invoice" element={<ProtectedRoute><Invoice /></ProtectedRoute>} />
      <Route path="/settings/client-team-notifications/contact-customer" element={<ProtectedRoute><ContactCustomer /></ProtectedRoute>} />
      <Route path="/settings/client-team-notifications/appointment-confirmation" element={<ProtectedRoute><AppointmentConfirmation /></ProtectedRoute>} />
      <Route path="/settings/client-team-notifications/estimate" element={<ProtectedRoute><Estimate /></ProtectedRoute>} />
      <Route path="/settings/client-team-notifications/appointment-rescheduled" element={<ProtectedRoute><AppointmentRescheduled /></ProtectedRoute>} />
      <Route path="/settings/client-team-notifications/appointment-reminder" element={<ProtectedRoute><AppointmentReminder /></ProtectedRoute>} />
      <Route path="/settings/client-team-notifications/assigned-job-cancelled" element={<ProtectedRoute><AssignedJobCancelled /></ProtectedRoute>} />
      <Route path="/settings/client-team-notifications/assigned-job-rescheduled" element={<ProtectedRoute><AssignedJobRescheduled /></ProtectedRoute>} />
      <Route path="/settings/client-team-notifications/team-member-invite" element={<ProtectedRoute><TeamMemberInvite /></ProtectedRoute>} />
      <Route path="/settings/client-team-notifications/recurring-assignment" element={<ProtectedRoute><RecurringAssignment /></ProtectedRoute>} />
      <Route path="/settings/client-team-notifications/job-offer" element={<ProtectedRoute><JobOffer /></ProtectedRoute>} />
      <Route path="/settings/client-team-notifications/job-assignment" element={<ProtectedRoute><JobAssignmentTeam /></ProtectedRoute>} />
      <Route path="/settings/job-assignment" element={<ProtectedRoute><JobAssignment /></ProtectedRoute>} />
      <Route path="/settings/availability" element={<ProtectedRoute><Availability /></ProtectedRoute>} />
      <Route path="/settings/availability/:location" element={<ProtectedRoute><LocationAvailability /></ProtectedRoute>} />
      <Route path="/settings/rescheduling-cancellation" element={<ProtectedRoute><ReschedulingCancellation /></ProtectedRoute>} />
      <Route path="/settings/developers" element={<ProtectedRoute><Developers /></ProtectedRoute>} />
      <Route path="/settings/calendar-syncing" element={<ProtectedRoute><CalendarSyncing /></ProtectedRoute>} />
      <Route path="/settings/google-sheets" element={<ProtectedRoute><GoogleSheetsSettings /></ProtectedRoute>} />
      <Route path="/settings/data-import" element={<ProtectedRoute><DataImportPage /></ProtectedRoute>} />
      {/* /settings/booking-koala now renders the full BookingKoala integration
          page (CSV importer + Live sync via Zapier Generate API Key). Previously
          it redirected to the generic /settings/data-import flow, which hid the
          Zapier Generate Key UI. Historic deep links still land here. */}
      <Route path="/settings/booking-koala" element={<ProtectedRoute><BookingKoalaIntegration /></ProtectedRoute>} />
      <Route path="/settings/taxes-fees" element={<ProtectedRoute><TaxesFees /></ProtectedRoute>} />
      <Route path="/settings/payments" element={<ProtectedRoute><PaymentsSettings /></ProtectedRoute>} />
      <Route path="/settings/payout-settings" element={<ProtectedRoute><PayoutSettings /></ProtectedRoute>} />
      <Route path="/settings/invoicing" element={<ProtectedRoute><Invoicing /></ProtectedRoute>} />
      <Route path="/settings/zenbooker" element={<ProtectedRoute><ZenbookerSettings /></ProtectedRoute>} />
      <Route path="/settings/leadbridge" element={<ProtectedRoute><LeadBridgeSettings /></ProtectedRoute>} />
      <Route path="/settings/proofpix" element={<ProtectedRoute><ProofPixIntegrationSettings /></ProtectedRoute>} />
      <Route path="/settings/identity-conflicts" element={<ProtectedRoute><IdentityConflictsPage /></ProtectedRoute>} />
      <Route path="/settings/service-areas" element={<ProtectedRoute><ServiceAreas /></ProtectedRoute>} />
      <Route path="/settings/booking-quote-requests" element={<ProtectedRoute><BookingQuoteRequests /></ProtectedRoute>} />
      <Route path="/settings/field-app" element={<ProtectedRoute><FieldApp /></ProtectedRoute>} />
      <Route path="/import-data" element={<ProtectedRoute><ImportDataPage /></ProtectedRoute>} />
      <Route path="/import-customers" element={<RedirectToDataImport defaultParams={{ type: 'customers' }} />} />
      <Route path="/import-jobs" element={<RedirectToDataImport defaultParams={{ type: 'jobs' }} />} />
      <Route path="/import-jobs-legacy" element={<ProtectedRoute><ImportJobsPage /></ProtectedRoute>} />
      </Route>
      {/* Public Booking Routes - No authentication required */}
      <Route path="/book/:userSlug" element={<PublicBooking />} />
      <Route path="/booking" element={<PublicBooking />} />
      <Route path="/quote/:userSlug" element={<PublicQuote />} />
      <Route path="/quote" element={<PublicQuote />} />
      <Route path="/demo-public-pages" element={<DemoPublicPages />} />
      <Route path="/customer-portal" element={<CustomerPortal />} />
      <Route path="/payment-success" element={<PaymentSuccess />} />
      
      {/* Public Invoice Payment Routes - No authentication required */}
      <Route path="public/invoice/:invoiceId" element={<InvoiceDisplay />} />
      <Route path="public/payment/:invoiceId" element={<PaymentProcessing />} />
      <Route path="public/payment-success/:invoiceId" element={<PaymentSuccess />} />
      
      {/* Team Member Routes — the old /dashboard and /field-app pages were
          replaced by the unified /dashboard with worker role gating. Legacy
          URLs redirect so bookmarks and push-notification clicks still work. */}
      <Route path="team-member/login" element={<LegacyTeamMemberLoginRedirect />} />
      <Route path="team-member/signup" element={<TeamMemberSignup />} />
      <Route path="team-member/dashboard" element={<Navigate to="/dashboard" replace />} />
      <Route path="team-member/field-app" element={<Navigate to="/dashboard" replace />} />

      {/* Password reset — public, used by both owner and team-member emails */}
      <Route path="reset-password" element={<ResetPasswordPage />} />
      <Route path="team-member/reset-password" element={<ResetPasswordPage />} />
      
      {/* Demo Routes */}
      <Route path="dropdown-demo" element={<DropdownMultiselectDemo />} />
      
      {/* Catch-all route - redirect to dashboard for any unmatched routes */}
      <Route path="*" element={<ProtectedRoute><ServiceFlowDashboard /></ProtectedRoute>} />
      </Routes>
      </CategoryProvider>
      </TimeFormatProvider>
      </LocationProvider>
    </AuthProvider>
  </BrowserRouter>
  </FixPromptBoundary>
  </QueryClientProvider>,
)
